import { ASSIGNED_RESOURCES, buildRbacUserSavePayload } from '../utils/assignedData'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import UbiquaTowerToastAlert from '../../components/alerts/UbiquaTowerToastAlert'
import LoadingScreen from '../../components/LoadingScreen'
import { isAuthRedirectError } from '../../auth'
import { MUI_TABLE_PAGINATION_OPTIONS } from '../../components/MuiTablePagination'
import RbacUserDetailContent from '../components/RbacUserDetailContent'
import RbacUserConfirmDialog from '../components/RbacUserConfirmDialog'
import RbacUserDetailHeader from '../components/RbacUserDetailHeader'
import RbacUserEditDialog from '../components/RbacUserEditDialog'
import { canManageRbacUsers } from '../../global_context/capabilities/rbacManagementCapabilities'
import {
  fetchRbacUser,
  fetchRbacUserHistory,
  resendRbacUserInvitation,
  updateRbacUser,
  updateRbacUserStatus
} from '../services/rbacApi'
import { fetchRoles } from '../roles/services/rolesApi'
import { getAssignableRbacRoles } from '../roles/shared/rbacRoleDetailHelpers'
import {
  RBAC_USER_DETAIL_TAB_VALUE,
  RBAC_USER_DETAIL_TEXTS,
  RBAC_USER_HISTORY_TAB_VALUE
} from './shared/rbacUserDetailConstants'
import {
  buildRbacUserProfileUpdatePayload,
  getRbacUserDisplayName,
  haveRbacUserRolesChanged
} from './shared/rbacUserDetailHelpers'
import {
  UI_COLOR_DANGER_ACTION,
  UI_COLOR_DANGER_ACTION_HOVER,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_HOVER
} from '../../constants/uiColors'

const DEFAULT_HISTORY_PER_PAGE = 10
const USERS_INDEX_PATH = '/configuracion/rbac/asignar'
const GENERIC_ERROR_MESSAGE =
  'Ocurrió un error al procesar la solicitud. Revisa tu conexión e intenta de nuevo.'

function buildStatusDialogConfig(user) {
  const inactive = user?.status === 'inactive'

  return {
    title: inactive
      ? RBAC_USER_DETAIL_TEXTS.activateModalTitle
      : RBAC_USER_DETAIL_TEXTS.deactivateModalTitle,
    body: inactive
      ? RBAC_USER_DETAIL_TEXTS.activateModalBody
      : RBAC_USER_DETAIL_TEXTS.deactivateModalBody,
    confirmLabel: inactive
      ? RBAC_USER_DETAIL_TEXTS.activateUser
      : RBAC_USER_DETAIL_TEXTS.deactivateUser,
    confirmColor: inactive ? UI_COLOR_PRIMARY : UI_COLOR_DANGER_ACTION,
    confirmHoverColor: inactive ? UI_COLOR_PRIMARY_HOVER : UI_COLOR_DANGER_ACTION_HOVER
  }
}

export default function RbacUserDetailPage() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const canManage = canManageRbacUsers()
  const [user, setUser] = useState(null)
  const [historyRows, setHistoryRows] = useState([])
  const [activeTab, setActiveTab] = useState(RBAC_USER_DETAIL_TAB_VALUE)
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [entryAnimationComplete, setEntryAnimationComplete] = useState(false)
  const [error, setError] = useState('')
  const [actionAlert, setActionAlert] = useState(null)
  const [editOpen, setEditOpen] = useState(false)
  const [roles, setRoles] = useState([])
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', roleIds: [] })
  const [editErrors, setEditErrors] = useState({})
  const [savingEdit, setSavingEdit] = useState(false)
  const [modalAlert, setModalAlert] = useState(null)
  const [statusOpen, setStatusOpen] = useState(false)
  const [savingStatus, setSavingStatus] = useState(false)
  const [statusDialogConfig, setStatusDialogConfig] = useState(() => buildStatusDialogConfig(null))
  const [resendOpen, setResendOpen] = useState(false)
  const [resending, setResending] = useState(false)
  const [historyPagination, setHistoryPagination] = useState({
    current_page: 1,
    per_page: DEFAULT_HISTORY_PER_PAGE,
    total_count: 0,
    total_pages: 1
  })

  const loadUser = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      setHistoryLoaded(false)
      setHistoryRows([])
      const requests = [fetchRbacUser(id)]
      if (canManage) requests.push(fetchRoles())
      const [userData, rolesData] = await Promise.all(requests)
      setUser(userData)
      if (rolesData) setRoles(rolesData)
      setLoading(false)
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setError(GENERIC_ERROR_MESSAGE)
      setLoading(false)
    }
  }, [canManage, id])

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true)
      const data = await fetchRbacUserHistory(id, {
        page: historyPagination.current_page,
        per_page: historyPagination.per_page
      })
      const totalCount = data.pagination?.total || 0
      setHistoryRows(data.results || [])
      setHistoryPagination((prev) => ({
        ...prev,
        total_count: totalCount,
        total_pages: Math.max(Math.ceil(totalCount / prev.per_page), 1)
      }))
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setError(GENERIC_ERROR_MESSAGE)
    } finally {
      setHistoryLoaded(true)
      setHistoryLoading(false)
    }
  }, [historyPagination.current_page, historyPagination.per_page, id])

  useEffect(() => {
    loadUser()
  }, [loadUser])

  useEffect(() => {
    if (activeTab === RBAC_USER_HISTORY_TAB_VALUE) loadHistory()
  }, [activeTab, loadHistory])

  const closeToastAlert = useCallback(() => {
    setActionAlert(null)
    setModalAlert(null)
    setError('')
  }, [])

  const backToUsers = () => {
    navigate(location.state?.from || USERS_INDEX_PATH)
  }

  const showActionAlert = (message, severity = 'success') => {
    setActionAlert({
      id: Date.now(),
      severity,
      message
    })
  }

  const openEdit = () => {
    setEditForm({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      roleIds: user?.role_ids || []
    })
    setEditErrors({})
    setModalAlert(null)
    setEditOpen(true)
  }

  const closeEdit = () => {
    setEditOpen(false)
    setEditErrors({})
    setModalAlert(null)
    setEditForm({ name: '', email: '', phone: '', roleIds: [] })
  }

  const updateEditField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }))
    setEditErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const validateEdit = () => {
    const nextErrors = {}
    if (!editForm.name.trim()) nextErrors.name = 'Campo obligatorio'
    if (!editForm.email.trim()) nextErrors.email = 'Campo obligatorio'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email.trim())) {
      nextErrors.email = 'Ingrese un correo electrónico válido'
    }
    if (editForm.roleIds.length === 0) nextErrors.roleIds = 'Escoja una opción'
    setEditErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const hasEditChanges = useMemo(() => {
    if (!user) return false

    const profilePayload = buildRbacUserProfileUpdatePayload(editForm, user)
    return (
      Object.keys(profilePayload).length > 0 ||
      editForm.assignedDataDirty ||
      haveRbacUserRolesChanged(editForm.roleIds, user.role_ids)
    )
  }, [editForm, user])

  const editDialogRoles = useMemo(
    () => getAssignableRbacRoles(roles, user?.roles),
    [roles, user?.roles]
  )

  const canSubmitEdit =
    editForm.name.trim() &&
    editForm.email.trim() &&
    editForm.roleIds.length > 0 &&
    hasEditChanges &&
    !savingEdit

  const saveEdit = async () => {
    if (!validateEdit()) return

    try {
      setSavingEdit(true)
      setError('')
      setModalAlert(null)
      const profilePayload = buildRbacUserProfileUpdatePayload(editForm, user)
      await updateRbacUser(
        id,
        buildRbacUserSavePayload(editForm, profilePayload, ASSIGNED_RESOURCES)
      )
      await loadUser()
      showActionAlert('Usuario actualizado correctamente.')
      closeEdit()
      if (activeTab === RBAC_USER_HISTORY_TAB_VALUE) loadHistory()
    } catch (err) {
      if (isAuthRedirectError(err)) return
      let message = err.message || GENERIC_ERROR_MESSAGE
      if (message === 'Failed to fetch') message = GENERIC_ERROR_MESSAGE

      setModalAlert({
        id: Date.now(),
        severity: 'error',
        title: 'No se pudo actualizar el usuario',
        message
      })
    } finally {
      setSavingEdit(false)
    }
  }

  const closeStatus = () => {
    if (savingStatus) return
    setStatusOpen(false)
  }

  const closeResend = () => {
    if (resending) return
    setResendOpen(false)
  }

  const saveStatus = async () => {
    const willActivate = user?.status === 'inactive'

    try {
      setSavingStatus(true)
      setError('')
      const updated = await updateRbacUserStatus(id, {
        active: willActivate,
        reason: willActivate ? null : 'admin'
      })
      setUser(updated)
      showActionAlert(
        willActivate ? 'Usuario activado correctamente.' : 'Usuario inactivado correctamente.'
      )
      setStatusOpen(false)
      setStatusDialogConfig(buildStatusDialogConfig(updated))
      if (activeTab === RBAC_USER_HISTORY_TAB_VALUE) loadHistory()
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setStatusOpen(false)
      setError(GENERIC_ERROR_MESSAGE)
    } finally {
      setSavingStatus(false)
    }
  }

  const resendInvitation = async () => {
    try {
      setResending(true)
      setError('')
      const updated = await resendRbacUserInvitation(id)
      setUser(updated)
      showActionAlert('Correo de activación reenviado.')
      setResendOpen(false)
      if (activeTab === RBAC_USER_HISTORY_TAB_VALUE) loadHistory()
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setResendOpen(false)
      setError(GENERIC_ERROR_MESSAGE)
    } finally {
      setResending(false)
    }
  }

  const handleHistoryPageChange = (_event, nextPage) => {
    setHistoryLoading(true)
    setHistoryPagination((prev) => ({ ...prev, current_page: nextPage }))
  }

  const handleHistoryRowsChange = (_event, nextRowsPerPage) => {
    const safeRowsPerPage = MUI_TABLE_PAGINATION_OPTIONS.includes(nextRowsPerPage)
      ? nextRowsPerPage
      : DEFAULT_HISTORY_PER_PAGE

    setHistoryLoading(true)
    setHistoryPagination((prev) => ({
      ...prev,
      per_page: safeRowsPerPage,
      current_page: 1
    }))
  }

  const handleTabChange = (nextTab) => {
    if (nextTab === activeTab) return
    setEntryAnimationComplete(true)
    if (nextTab === RBAC_USER_HISTORY_TAB_VALUE) setHistoryLoading(true)
    setActiveTab(nextTab)
  }

  if (loading) return <LoadingScreen text={RBAC_USER_DETAIL_TEXTS.loading} />

  const displayName = getRbacUserDisplayName(user)
  const pageTitle = user ? `Usuario: ${user.code || id || '—'} - ${displayName}` : `Usuario: ${id}`
  const shouldShowHistoryLoading =
    historyLoading || (activeTab === RBAC_USER_HISTORY_TAB_VALUE && !historyLoaded)
  const toastAlert =
    editOpen && modalAlert
      ? modalAlert
      : editOpen || statusOpen || resendOpen
        ? null
        : error
          ? { id: `rbac-user-error-${error}`, severity: 'error', message: error }
          : actionAlert

  return (
    <>
      <UbiquaTowerToastAlert
        key={toastAlert?.id ?? toastAlert?.message}
        open={Boolean(toastAlert)}
        id={toastAlert?.id}
        severity={toastAlert?.severity}
        title={toastAlert?.title}
        message={toastAlert?.message}
        onClose={closeToastAlert}
      />
      <div
        className="app-detail-shell ubiqua-type app-content-enter"
        data-entry-complete={entryAnimationComplete}
        onAnimationEnd={(event) => {
          if (event.target === event.currentTarget && event.animationName === 'app-content-rise') {
            setEntryAnimationComplete(true)
          }
        }}
      >
        <RbacUserDetailHeader
          title={pageTitle}
          backLabel={RBAC_USER_DETAIL_TEXTS.backToUsersLabel}
          onBack={backToUsers}
        />

        <RbacUserDetailContent
          error={error}
          user={user}
          canManage={canManage}
          activeTab={activeTab}
          historyRows={historyRows}
          historyLoading={shouldShowHistoryLoading}
          historyPagination={historyPagination}
          resending={resending}
          onBack={backToUsers}
          onTabChange={handleTabChange}
          onEditUser={openEdit}
          onStatusAction={() => {
            setStatusDialogConfig(buildStatusDialogConfig(user))
            setStatusOpen(true)
          }}
          onResendInvitation={() => setResendOpen(true)}
          onHistoryPageChange={handleHistoryPageChange}
          onHistoryRowsPerPageChange={handleHistoryRowsChange}
        />
      </div>

      <RbacUserEditDialog
        open={editOpen}
        onClose={closeEdit}
        onSubmit={saveEdit}
        roles={editDialogRoles}
        editTarget={user}
        form={editForm}
        errors={editErrors}
        onFieldChange={updateEditField}
        submitting={savingEdit}
        canSubmit={canSubmitEdit}
      />

      {statusOpen && (
        <RbacUserConfirmDialog
          open={statusOpen}
          onClose={closeStatus}
          onConfirm={saveStatus}
          user={user}
          title={statusDialogConfig.title}
          body={statusDialogConfig.body}
          confirmLabel={statusDialogConfig.confirmLabel}
          confirmColor={statusDialogConfig.confirmColor}
          confirmHoverColor={statusDialogConfig.confirmHoverColor}
          submitting={savingStatus}
          loadingLabel="Guardando..."
        />
      )}

      <RbacUserConfirmDialog
        open={resendOpen}
        onClose={closeResend}
        onConfirm={resendInvitation}
        user={user}
        title={RBAC_USER_DETAIL_TEXTS.resendModalTitle}
        notice={RBAC_USER_DETAIL_TEXTS.resendModalNotice}
        body={RBAC_USER_DETAIL_TEXTS.resendModalBody}
        confirmLabel={RBAC_USER_DETAIL_TEXTS.resendEmail}
        submitting={resending}
        loadingLabel={RBAC_USER_DETAIL_TEXTS.resendingEmail}
      />
    </>
  )
}
